import { ActionIcon, Badge, Loader, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowMoveRight,
  IconDownload,
  IconExternalLink,
  IconLink,
  IconPencil,
  IconShare,
  IconTrash,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DirectorySelectionModal } from 'src/Components/Modals/DirectorySelectionModal';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { TextInputModal } from 'src/Components/Modals/TextInputModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useCreateFileExport } from 'src/Hooks/Mutations/useCreateFileExport';
import { useMoveDocument } from 'src/Hooks/Mutations/useMoveDocument';
import { useRenameStorageItem } from 'src/Hooks/Mutations/useRenameStorageItem';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useCanCreate, useHasPermission } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { usePermissionDeniedModal } from 'src/Hooks/Shared/usePermissionDeniedModal';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';
import { AssetStorageItemDtoTypeEnum, DocumentAnalysisStatus } from 'src/Services/API/generated';
import humanizeFileSize from 'src/Utils/humanizeFileSize';

import styles from './DocumentFiles.module.scss';
import { Props } from './DocumentFiles.types';

const DocumentFiles = ({
  files,
  loading,
  compact,
  onDeleteFile,
  onFileClick,
  onDownloadFile,
  selectedRecords,
  onSelectedRecordsChange,
  suggestionStatusByFileId,
  onSuggestionClick,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [renameModalOpen, { open: openRenameActionModal, close: closeRenameActionModal }] =
    useDisclosure();
  const [moveModalOpen, { open: openMoveActionModal, close: closeMoveActionModal }] =
    useDisclosure();
  const [actionId, setActionId] = useState<string | undefined>();
  const [actionFileName, setActionFileName] = useState<string | undefined>();
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportName, setExportName] = useState('');
  const [exportedFileUrl, setExportedFileUrl] = useState('');
  const exportActionRef = useRef<'modal' | 'copy'>('modal');

  const { mutate: move, isLoading } = useMoveDocument({
    onSettled: () => {
      closeMoveActionModal();
    },
  });

  const { mutate: createFileExport, isLoading: isCreatingExport } = useCreateFileExport({
    onSuccess: (data, variables) => {
      const fileId = variables[0];
      const file = files.find(f => f.id === fileId);

      if (exportActionRef.current === 'copy') {
        navigator.clipboard.writeText(data.publicUrl);
        notifications.show({
          message: t('documents.share.linkCopied', 'Lien copié !'),
        });
        return;
      }

      setExportedFileUrl(data.publicUrl);
      setExportName(file?.name ?? t('documents.share.defaultExportName', 'Partage de fichier'));

      setShowExportModal(true);
    },
    onError: () => {
      console.error('Failed to create file export.');
    },
  });

  const { mutate: rename, isLoading: isRenameLoading } = useRenameStorageItem({
    onSettled: () => {
      closeRenameActionModal();
    },
  });

  const getExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.slice(lastDot) : '';
  };

  const stripExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.slice(0, lastDot) : filename;
  };

  const assetAboveQuotas = useIsAssetAboveQuotas();
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(files);

  // Permission checks
  const { data: currentUser } = useFetchUser();
  const { hasPermission: canExport } = useCanCreate('entityExport');
  const { permissionLevel, ownerName } = useHasPermission('assetStorage', 'read');
  const showPermissionDeniedModal = usePermissionDeniedModal(ownerName);

  // Check if user can modify a specific file (based on ownership)
  const canModifyFile = (fileCreatedById?: string) => {
    if (permissionLevel === 'full') return true;
    if (permissionLevel === 'own' && fileCreatedById && currentUser?.id) {
      return fileCreatedById === currentUser.id;
    }
    return false;
  };

  return (
    <div className={styles.container}>
      <QRCodeModal
        open={showExportModal}
        url={exportedFileUrl}
        onClose={() => setShowExportModal(false)}
        exportName={exportName}
      />
      <TextInputModal
        open={renameModalOpen}
        onClose={() => closeRenameActionModal()}
        title={t('documents.rename.title', 'Renommer')}
        subtitle={''}
        placeholder={t('documents.rename.newName', 'Nouveau nom du fichier')}
        buttonText={t('documents.rename.rename', 'Renommer')}
        loading={isRenameLoading}
        defaultValue={actionFileName ? stripExtension(actionFileName) : undefined}
        onValidate={text => {
          if (actionId) {
            const ext = actionFileName ? getExtension(actionFileName) : '';
            rename({ itemId: actionId, name: text + ext });
          }
        }}
      />
      <DirectorySelectionModal
        open={moveModalOpen}
        onClose={() => closeMoveActionModal()}
        title={`${t('documents.move.title', 'Déplacer')}`}
        subtitle={t('documents.move.subtitle', 'Choisissez le dossier de destination')}
        buttonText={t('documents.move.move', 'Déplacer')}
        loading={isLoading}
        onValidate={value => {
          if (actionId) {
            move({ path: value.path, fileId: actionId });
          }
        }}
      />
      <DataTable
        width={'100%'}
        withRowBorders={false}
        records={sortedRecords}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        noRecordsText={''}
        fetching={loading}
        onRowClick={({ record }) => onFileClick(record.id)}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={onSelectedRecordsChange}
        columns={[
          {
            accessor: 'name',
            title: t('documents.table.name', 'Nom'),
            sortable: true,
            cellsClassName: styles.filename,
            ellipsis: true,
            width: '100%',
            cellsStyle: () => ({
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }),
            render: ({ id, name, type }) => {
              const suggestionStatus = suggestionStatusByFileId?.get(id);

              return (
                <div className={styles.nameCell}>
                  {type === AssetStorageItemDtoTypeEnum.ExternalLink && (
                    <IconExternalLink size={14} className={styles.externalLinkIcon} />
                  )}
                  <span className={styles.name}>{name}</span>
                  {suggestionStatus === DocumentAnalysisStatus.Analyzing && (
                    <Badge
                      variant="light"
                      color="gray"
                      leftSection={<Loader size={10} color="gray" />}
                      className={styles.suggestionBadge}
                    >
                      {t('documents.suggestions.badgeAnalyzing', 'Lecture…')}
                    </Badge>
                  )}
                  {suggestionStatus === DocumentAnalysisStatus.Pending && (
                    <Tooltip
                      label={t(
                        'documents.suggestions.badgeTooltip',
                        'Un nom et un dossier vous sont proposés'
                      )}
                    >
                      <Badge
                        component="button"
                        variant="light"
                        className={`${styles.suggestionBadge} ${styles.suggestionBadgeClickable}`}
                        onClick={event => {
                          event.stopPropagation();
                          onSuggestionClick?.();
                        }}
                      >
                        {t('documents.suggestions.badgePending', 'À ranger')}
                      </Badge>
                    </Tooltip>
                  )}
                </div>
              );
            },
          },
          {
            accessor: 'uploader',
            width: '20%',
            title: t('documents.table.uploader', 'Ajouté par'),
            render: ({ createdBy }) => (
              <div className={styles.rowItem}>
                {[createdBy?.firstName, createdBy?.lastName].join(' ')}
              </div>
            ),
            hidden: compact,
          },
          {
            accessor: 'size',
            width: '10%',
            title: t('documents.table.size', 'Taille'),
            sortable: true,
            render: ({ size, type }) =>
              type === AssetStorageItemDtoTypeEnum.File && (
                <div className={styles.rowItem}>{humanizeFileSize(size)}</div>
              ),
            hidden: compact,
          },
          {
            accessor: 'lastModified',
            width: '20%',
            title: t('documents.table.lastModified', 'Dernière modification'),
            render: ({ updatedAt }) => (
              <div className={styles.rowItem}>{updatedAt.locale(i18n.language).format('LL')}</div>
            ),
            hidden: compact,
          },
          {
            accessor: 'actions',
            title: '',
            width: 90,
            render: ({ id, name, createdBy, type }) => {
              const canModify = canModifyFile(createdBy?.id);
              const isExternalLink = type === AssetStorageItemDtoTypeEnum.ExternalLink;
              return (
                <div className={styles.actionsCell}>
                  {canExport && !isExternalLink && (
                    <Tooltip label={t('documents.menu.copyLink', 'Copier le lien')} withArrow>
                      <ActionIcon
                        variant={'transparent'}
                        disabled={isCreatingExport}
                        onClick={e => {
                          e.stopPropagation();
                          exportActionRef.current = 'copy';
                          createFileExport([id]);
                        }}
                      >
                        <IconLink className={styles.iconAction} size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <RowActions
                    actions={[
                      {
                        key: 'download',
                        label: t('documents.menu.download', 'Télécharger'),
                        icon: <IconDownload size={18} />,
                        hidden: isExternalLink,
                        onClick: () => onDownloadFile(id),
                      },
                      {
                        key: 'share',
                        label: t('documents.menu.share', 'Partager'),
                        icon: <IconShare size={18} />,
                        hidden: !canExport || isExternalLink,
                        disabled: isCreatingExport,
                        onClick: () => {
                          exportActionRef.current = 'modal';
                          createFileExport([id]);
                        },
                      },
                      {
                        key: 'rename',
                        label: t('documents.menu.rename', 'Renommer'),
                        icon: <IconPencil size={18} />,
                        disabled: assetAboveQuotas,
                        onClick: () => {
                          if (!canModify) {
                            showPermissionDeniedModal();
                            return;
                          }
                          setActionId(id);
                          setActionFileName(name);
                          openRenameActionModal();
                        },
                      },
                      {
                        key: 'move',
                        label: t('documents.menu.move', 'Déplacer'),
                        icon: <IconArrowMoveRight size={18} />,
                        hidden: isExternalLink,
                        disabled: assetAboveQuotas,
                        onClick: () => {
                          if (!canModify) {
                            showPermissionDeniedModal();
                            return;
                          }
                          setActionId(id);
                          openMoveActionModal();
                        },
                      },
                      {
                        key: 'delete',
                        label: t('documents.menu.delete', 'Supprimer'),
                        icon: <IconTrash size={18} />,
                        variant: 'danger',
                        onClick: () => {
                          if (!canModify) {
                            showPermissionDeniedModal();
                            return;
                          }
                          onDeleteFile(id);
                        },
                      },
                    ]}
                  />
                </div>
              );
            },
            textAlign: 'right',
          },
        ]}
        styles={{
          table: {
            tableLayout: 'fixed',
            width: '100%',
          },
        }}
        style={{ width: '100%', minHeight: '10rem' }}
      />
    </div>
  );
};

export default DocumentFiles;
